import * as Sentry from '@sentry/react';
import { StyledAvatar, Text, Tooltip, useToast } from '@televet/kibble-ui';
import { Flex, Tr } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useTracking } from 'shared/providers/TrackingProvider';
import { openChannelOverlay } from 'pages/Conversations/state/conversationsSlice';
import { TaskWithMemoizedValues } from 'pages/Tasks/types';
import React, { useCallback, useState } from 'react';
import { AvatarGroupSelect } from 'shared/components/Avatars/AvatarGroupSelect';
import { useAvatarGroupSelectOptions } from 'shared/components/Avatars/AvatarGroupSelect/useAvatarGroupSelectOptions';
import { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { TasksEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';
import { TaskStatus, useUpdateOneTaskMutation } from 'shared/types/graphql';
import { useAppDispatch } from 'state/hooks';
import { TaskExpandableDetailCell } from './TaskExpandableDetailCell';
import { TaskStatusSelector } from '../TaskStatusSelector/TaskStatusSelector';
import { TaskTableColumn } from './TaskTableColumn';

type TaskRowProps = {
  data: TaskWithMemoizedValues;
  elementRef: (el: HTMLTableRowElement | null) => void;
  style: React.CSSProperties;
  slideOut: (id: string) => void;
  borderLeftColor?: string;
  completedTasksOnly?: boolean;
  onStatusChange: () => void;
  onTaskEdit?: (task: TaskWithMemoizedValues) => void;
};

export const TaskRow = ({
  borderLeftColor,
  completedTasksOnly,
  data: task,
  elementRef,
  style,
  slideOut,
  onStatusChange,
  onTaskEdit,
}: TaskRowProps): JSX.Element => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { clinicUserId, currentClinicId } = useClinicUser();
  const { viewClinicPetParent } = useSidePanelSearch();
  const { trackEvent } = useTracking();

  const avatarGroupData = useAvatarGroupSelectOptions({
    selectedUserIds:
      completedTasksOnly && task.status === TaskStatus.Completed && task.completedByUserId
        ? [task.completedByUserId]
        : task.assigneeIds,
  });
  const [updateTask, { loading: isUpdateTaskLoading }] = useUpdateOneTaskMutation();

  const [updatingTaskStatus, setUpdatingTaskStatus] = useState(false);

  const navigateToConversation = useCallback(() => {
    trackEvent(TasksEvents.MessageButtonClicked, {
      clinicId: currentClinicId,
    });
    dispatch(openChannelOverlay({ petParentId: task.clinicPetParent?.id }));
  }, [currentClinicId, dispatch, task.clinicPetParent?.id, trackEvent]);

  const handleAssigneeToggle = useCallback(
    (e: React.MouseEvent, option: IOption) => {
      if (isUpdateTaskLoading) {
        return;
      }
      const assigneeUserId = option.value.id;

      updateTask({
        variables: {
          where: {
            id: task.id,
          },
          data: {
            assignees: {
              connect: option.isSelected ? [] : [{ id: assigneeUserId }],
              disconnect: option.isSelected ? [{ id: assigneeUserId }] : [],
            },
          },
        },
      });
      trackEvent(TasksEvents.AssigneesUpdated, {
        clinicId: currentClinicId,
      });
    },
    [isUpdateTaskLoading, updateTask, task.id, trackEvent, currentClinicId],
  );

  const updateCompletedStatus = useCallback(
    async (isCompleted: boolean) => {
      try {
        if (isUpdateTaskLoading) {
          return;
        }
        setUpdatingTaskStatus(true);

        await updateTask({
          variables: {
            where: {
              id: task.id,
            },
            data: {
              status: isCompleted ? TaskStatus.Completed : TaskStatus.Open,
              completedAt: isCompleted ? new Date() : null,
              completedByUserId: isCompleted ? clinicUserId : null,
            },
          },
        });
        trackEvent(isCompleted ? TasksEvents.MarkedCompleted : TasksEvents.MarkedUndone, {
          clinicId: currentClinicId,
        });
        setTimeout(() => {
          slideOut(task.id);
        }, 750);
      } catch (e) {
        Sentry.captureException(e);
        toast({
          status: 'error',
          description: 'Oops, an error occurred while updating the task.',
        });
        console.error(e);
        setUpdatingTaskStatus(false);
      }
    },
    [clinicUserId, currentClinicId, isUpdateTaskLoading, slideOut, task.id, toast, trackEvent, updateTask],
  );

  const _onStatusChange = useCallback(
    async (status: TaskStatus) => {
      if (status === TaskStatus.Completed) {
        setTimeout(() => {
          slideOut(task.id);
        }, 750);
      } else {
        onStatusChange();
      }
    },
    [onStatusChange, slideOut, task.id],
  );

  return (
    <Tr style={style} ref={elementRef}>
      <TaskTableColumn minWidth="375px" maxWidth="375px" pr={2} alignContent="center">
        <TaskExpandableDetailCell task={task} borderLeftColor={borderLeftColor} />
      </TaskTableColumn>
      {completedTasksOnly ? (
        <>
          <TaskTableColumn minWidth="185px">
            {task.clinicPetParent && (
              <Flex
                gap={1}
                alignItems="center"
                cursor="pointer"
                onClick={(): void => {
                  viewClinicPetParent({ clinicPetParentId: task.clinicPetParent?.id || '' });
                }}
                data-testid={`task-table-clientName-${task.id}`}
              >
                <StyledAvatar
                  size="xs"
                  name={task['clinicPetParent.firstName']}
                  type="Client"
                  handleClick={(): void => {
                    viewClinicPetParent({ clinicPetParentId: task.clinicPetParent?.id || '' });
                  }}
                />
                <Text size="sm">{task['clinicPetParent.firstName']}</Text>
              </Flex>
            )}
          </TaskTableColumn>

          <TaskTableColumn>
            <Text size="sm" data-testid={`task-table-completedAt-${task.id}`}>
              {task.completedAtReadableTime}
            </Text>
          </TaskTableColumn>

          <TaskTableColumn borderRightWidth="0px" minWidth="220px">
            <Flex alignItems="center" justifyContent="space-between">
              <AvatarGroupSelect
                options={avatarGroupData}
                avatarSize="xs"
                popoverPlacement={PopoverPlacement.BottomEnd}
                onToggle={handleAssigneeToggle}
                showRemoveButton={false}
                isAddingAvatar={false}
              />
              <Flex gap={1} pr={1}>
                {onTaskEdit && (
                  <Button size="sm" iconName="pen" variant="tertiary" onClick={(): void => onTaskEdit(task)} />
                )}
                <Button
                  size="sm"
                  iconName="checkmark"
                  variant="tertiary"
                  iconPosition="right"
                  borderColor="border.default"
                  disabled={updatingTaskStatus}
                  onClick={(): void => {
                    updateCompletedStatus(false);
                  }}
                  data-testid={`task-table-undo-button-${task.id}`}
                >
                  Undo
                </Button>
              </Flex>
            </Flex>
          </TaskTableColumn>
        </>
      ) : (
        <>
          <TaskTableColumn>
            <TaskStatusSelector task={task} onStatusChange={_onStatusChange} showBadge={true} />
          </TaskTableColumn>
          <TaskTableColumn minWidth="185px">
            {task.clinicPetParent && (
              <Flex
                gap={1}
                alignItems="center"
                cursor="pointer"
                onClick={(): void => {
                  viewClinicPetParent({ clinicPetParentId: task.clinicPetParent?.id || '' });
                }}
                data-testid={`task-table-clientName-${task.id}`}
              >
                <StyledAvatar
                  size="xs"
                  name={task['clinicPetParent.firstName']}
                  type="Client"
                  handleClick={(): void => {
                    viewClinicPetParent({ clinicPetParentId: task.clinicPetParent?.id || '' });
                  }}
                />
                <Text size="sm">{task['clinicPetParent.firstName']}</Text>
              </Flex>
            )}
          </TaskTableColumn>

          <TaskTableColumn>
            <Text size="sm" data-testid={`task-table-createdAt-${task.id}`}>
              {task.createdAtReadableTime}
            </Text>
          </TaskTableColumn>

          <TaskTableColumn borderRightWidth="0px" minWidth="220px">
            <Flex alignItems="center" justifyContent="space-between">
              <AvatarGroupSelect
                options={avatarGroupData}
                avatarSize="xs"
                popoverPlacement={PopoverPlacement.BottomEnd}
                addButtonTooltip="Assign team member"
                onToggle={handleAssigneeToggle}
                showRemoveButton={true}
              />
              <Flex gap={1} pr={1}>
                {onTaskEdit && (
                  <Button size="sm" iconName="pen" variant="tertiary" onClick={(): void => onTaskEdit(task)} />
                )}
                {task.clinincPetParentId ? (
                  <Button
                    size="sm"
                    iconName="message"
                    variant="secondary"
                    borderColor="border.default"
                    onClick={navigateToConversation}
                    data-testid={`task-table-message-button-${task.id}`}
                  />
                ) : (
                  <Tooltip label="Add a client to  this task to start a conversation." placement="top-end">
                    <Flex>
                      <Button
                        size="sm"
                        iconName="message"
                        variant="secondary"
                        borderColor="border.default"
                        data-testid={`task-table-message-button-${task.id}`}
                        isDisabled={true}
                      />
                    </Flex>
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          </TaskTableColumn>
        </>
      )}
    </Tr>
  );
};
